import React from 'react';
import styled, { css } from '../../styles/styled-components';
import { darken, lighten } from 'polished';
import { Glyphicon, IconsKey } from '../icons';
import { LoaderIcon } from '../loader';
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';

export type ButtonProps = {
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  primary?: boolean;
  bordered?: boolean;
  transparent?: boolean;
  disabled?: boolean;
  loading?: boolean;
  icon?: IconsKey | React.ReactElement<any>;
  faIconProps?: FontAwesomeIconProps;
  content?: React.ReactChild;
  size?: Size;
  title?: string;
  type?: 'submit' | 'reset' | 'button';
} & Stylable;

export const Button: React.SFC<ButtonProps> = ({
  children,
  icon,
  faIconProps,
  content,
  size = 'normal' as Size,
  disabled,
  loading,
  title,
  ...rest
}) => {
  const renderContent = () => {
    if (children) {
      return children;
    } else if (loading) {
      return [
        <LoaderIcon
          key="icon"
          style={{ marginRight: !!content ? '5px' : '0' }}
          size={size}
        />,
        content,
      ];
    } else if (faIconProps) {
      return [
        <FontAwesomeIcon
          key="icon"
          title={title}
          {...faIconProps}
          style={{ marginRight: !!content ? '5px' : '0' }}
        />,
        content,
      ];
    } else if (icon && typeof icon !== 'string') {
      return [icon, content];
    } else if (!!icon) {
      return [
        <ButtonIcon
          key="icon"
          style={{ marginRight: !!content ? '5px' : '0' }}
          glyph={icon as IconsKey}
          size={size}
        />,
        content,
      ];
    } else {
      return content;
    }
  };

  return (
    <StyledButton
      size={size}
      disabled={loading || disabled}
      loading={loading}
      title={title}
      {...rest}
    >
      {renderContent()}
    </StyledButton>
  );
};

type StyledButtonProps = {
  primary?: boolean;
  transparent?: boolean;
  bordered?: boolean;
  loading?: boolean;
  size: Size;
} & JSX.IntrinsicElements['button'];

export const StyledButton = styled<StyledButtonProps>(
  ({ primary, bordered, size, transparent, loading, ...rest }) => (
    <button {...rest} />
  ),
)`
  border: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${p => p.theme.grayBackground};
  border-radius: 2px;

  &:hover {
    background-color: ${p => darken(0.1)(p.theme.grayBackground)};
  }

  // Size
  ${p => {
    switch (p.size) {
      case 'normal':
        return css`
          padding: 10px;
          font-size: 16px;
        `;
      case 'small':
        return css`
          padding: 7px 9px;
          font-size: 14px;
        `;
    }
  }};

  ${p => {
    if (p.loading) {
      return css`
        background-color: ${p.theme.grayBackground};
        cursor: not-allowed;

        &:hover {
          background-color: ${p.theme.grayBackground};
        }
      `;
    }

    if (p.disabled) {
      return css`
        background-color: ${lighten(0.05, p.theme.grayBackground)};
        // background-color: ${p.theme.grayBackground};
        cursor: not-allowed !important;
        color: ${lighten(0.7, p.theme.blackText)};

        &:hover {
          background-color: ${lighten(0.05, p.theme.grayBackground)};
        }
      `;
    }

    if (p.primary)
      return css`
        background-color: ${p.theme.primaryColor};
        color: white;

        &:hover {
          background-color: ${darken(0.1, p.theme.primaryColor)};
        }
      `;

    if (p.bordered)
      return css`
        color: ${p.theme.primaryColor};
        border: 2px solid ${p.theme.primaryColor};
        // To subtract the border width
        padding: calc(10px - 2px);
      `;

    if (p.transparent)
      return css`
        background-color: transparent;
        color: ${p.theme.blackText};

        &:hover {
          background-color: ${p.theme.grayBackground};
        }
      `;
  }};
`;

export const ButtonIcon = styled(Glyphicon)``;

type A11yButtonProps = {
  linkLook?: boolean;
} & JSX.IntrinsicElements['button'];

export const A11yButton = styled<A11yButtonProps>(({ linkLook, ...rest }) => (
  <button {...rest} />
))`
  border: 0;
  width: 100%;
  height: 100%;
  background-color: transparent;
  text-align: left;
  padding: 0;
  cursor: pointer;

  ${p =>
    p.linkLook
      ? css`
          color: #005999;

          body:not(.user-is-tabbing) &:focus {
            outline: none;
          }

          &:hover {
            color: #3af;
          }
        `
      : ''};
`;
